import type React from 'react';
import Link from 'next/link';
import type { ComponentProps } from 'react';
import {
  Box,
  designTokens,
  Panel,
  PanelBody,
  PanelHeader
} from '@itandi/itandi-bb-ui';
import { DefaultV2 } from '~/components/common/layouts/DefaultV2';
import { DealLayout } from '~/components/common/layouts/deals/DealLayout';
import Styles from './Page.module.scss';
import { useDeals } from '~/hooks/useDeals';
import { PATH as DEALS_PATH } from '~/pages/deals';
import Loading from '~/components/common/parts/loading/Loading';
import { PATH } from '~/pages/deals/[id]';
import { useIdQueryHandler } from '~/hooks/useIdQueryHandler';
import { useDealEstimationHeaders } from '~/hooks/deals/useDealEstimationHeaders';
import { useDealEstimationConfirmedHeaders } from '~/hooks/deals/useDealEstimationConfirmedHeaders';
import { ConstructionTop } from '~/components/common/constructions/top/ConstructionTop';
import { getDownloadPath } from '~/utils/api/deals/DealEstimationHistoryConfirmedFile';
import { isOwnStatus } from '~/models/Status';
import { ConstructionConfirmedList } from '~/components/common/constructions/confirmed_list/ConstructionList';
import { DealEstimationHeaderStatus } from '~/components/common/deal_estimation_headers/DealEstimationHeaderStatus';
import { showNewFeatureForLocal } from '~/utils/FeatureFlagUtil';

export const Page: React.FC = () => {
  const id = useIdQueryHandler();
  const headerId = useIdQueryHandler('headerId');
  const { data: deal } = useDeals().show(id);
  const { data: dealEstimationHeaders } = useDealEstimationHeaders().index(id);
  const { data: dealEstimationHeaderContext } =
    useDealEstimationConfirmedHeaders().show(id, headerId);
  if (
    id === null ||
    headerId === null ||
    deal === null ||
    dealEstimationHeaders === null ||
    dealEstimationHeaderContext === null
  ) {
    return <Loading isLoading />;
  }
  const dealEstimationHeader =
    dealEstimationHeaders.find((header) => header.id === headerId) ?? null;
  const handleClickPreview = (): void => {
    const path = getDownloadPath(id, headerId);
    window.open(path);
  };
  const items = [
    { label: '案件一覧', href: DEALS_PATH, as: Link },
    { label: '案件詳細', href: PATH(deal.id), as: Link },
    { label: '見積り履歴確認' }
  ] as const satisfies ComponentProps<typeof DealLayout>['items'];
  if (dealEstimationHeader === null) return null;
  return (
    <DefaultV2>
      <DealLayout deal={deal} isVendor={false} items={items}>
        <div className={Styles.EstimateHeader}>
          <h1 className={Styles.EstimateHeaderTitle}>見積り履歴確認</h1>
          <DealEstimationHeaderStatus
            dealEstimationHeader={dealEstimationHeader}
            ownManagement={isOwnStatus(deal.status)}
          />
        </div>
        <ConstructionTop
          constructionContext={dealEstimationHeaderContext}
          handleClickEstimationHistoryPreview={handleClickPreview}
          readonly
        />
        <ConstructionConfirmedList
          companyType="client"
          constructionContext={dealEstimationHeaderContext}
        />
        {deal.estimationBy === 'vendor' && showNewFeatureForLocal() && (
          <Box mt={designTokens['spacing/l']}>
            <Panel>
              <PanelHeader title="備考" />
              <PanelBody>{dealEstimationHeader.note}</PanelBody>
            </Panel>
          </Box>
        )}
      </DealLayout>
    </DefaultV2>
  );
};
